import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardStep, OnboardingWizardProps } from '@/features/onboarding/types/onboarding.types';
import { useOnboardingAutoLogin } from '@/features/onboarding/hooks/useOnboardingAutoLogin';
import * as ROUTES from '@/shared/constants/routes';
import { WelcomeStep } from './steps/WelcomeStep';
import { SetupPasswordStep } from './steps/SetupPasswordStep';
import { ActivateCardStep } from './steps/ActivateCardStep';
import { HasPhysicalCardStep } from './steps/HasPhysicalCardStep';
import { SuccessStep } from './steps/SuccessStep';
import styles from './Onboarding.module.css';
import { t } from '@/i18n';


export function OnboardingWizard({ reference, user }: OnboardingWizardProps) {
	const [currentStep, setCurrentStep] = useState<WizardStep>(t('onboarding.steps.welcome'));
	const passwordRef = useRef<string>('');
	const navigate = useNavigate();
	const { runAutoLogin } = useOnboardingAutoLogin();

	const goToPasswordSetup = () => {
		setCurrentStep(t('onboarding.steps.setupPassword'));
	};

	const goToPhysicalCard = () => {
		setCurrentStep(t('onboarding.steps.physicalCard'));
	};

	const goToCardActivation = () => {
		setCurrentStep(t('onboarding.steps.activateCard'));
	};

	const goToSuccess = () => {
		setCurrentStep(t('onboarding.steps.success'));
	};

	// Cierre del onboarding: si el auto-login está activo inicia sesión (y enruta
	// al login/MFA como el login normal). Si está apagado o falla, cae al fallback
	// que mantiene el comportamiento original.
	const finishOnboarding = async (fallback: () => void) => {
		const loggedIn = await runAutoLogin(user?.email, passwordRef.current);
		if (!loggedIn) fallback();
	};

	const handleNoCard = () => finishOnboarding(() => navigate(ROUTES.LOGIN));
	const handleCardActivated = () => finishOnboarding(goToSuccess);

	return (
		<div className={styles.wizardContainer}>
			{currentStep === t('onboarding.steps.welcome') && (
				<WelcomeStep onSuccess={goToPasswordSetup} />
			)}
			{currentStep === t('onboarding.steps.setupPassword') && (
				<SetupPasswordStep
					reference={reference}
					onPasswordSet={(password) => { passwordRef.current = password; }}
					onSuccess={goToPhysicalCard}
				/>
			)}
			{currentStep === t('onboarding.steps.physicalCard') && (
				<HasPhysicalCardStep onSuccess={goToCardActivation} onNoCard={handleNoCard} />
			)}
			{currentStep === t('onboarding.steps.activateCard') && (
				<ActivateCardStep user={user} onSuccess={handleCardActivated} />
			)}
			{currentStep === t('onboarding.steps.success') && (
				<SuccessStep />
			)}
		</div>
	);
}
